import { useState } from 'react'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { PersonOutlineIcon, LocationOutlinedIcon } from '../UI/Icons'

const DECLINED_REASON_LABELS: Record<string, string> = {
  NoAvailability: 'Venue unavailable',
  LowBudget: "Doesn't meet your budget",
  CateringRequirements: "Doesn't meet your catering needs",
  FacilitiesRequirements: "Doesn't meet your facility needs",
  Other: 'Not available',
}

export interface PreviousEventRequest {
  spaceName: string
  venueName: string
  imageUrl?: string
  location?: string
  spaceId?: number
  hostId?: number
  declinedReason?: string | null
  lat?: number
  lng?: number
}

const VISIBLE_REQUEST_COUNT = 2

export interface PreviousEvent {
  id: string
  bookingName: string
  bookedBy: string
  date: string
  invoiceAmount?: string
  currency?: string
  location?: string
  lat?: number
  lon?: number
  country?: string
  radiusKm?: number
  locationPolygon?: string
  capacity?: string
  bookedVenue?: PreviousEventRequest
  supplierName?: string
  requests?: PreviousEventRequest[]
  layoutId?: string
  cateringNeeds?: string
  additionalNotes?: string
  isFlexibleWithDates?: boolean
  flexibilityNotes?: string
  eventTypeId?: string
  styleIds?: string[]
  facilityIds?: number[]
}

interface Props {
  event: PreviousEvent
  isSelected: boolean
  onSelect: () => void
}

const PreviousEventCard = ({ event, isSelected, onSelect }: Props) => {
  const [showAll, setShowAll] = useState(false)
  const requests = event.requests ?? []
  const visibleRequests = showAll ? requests : requests.slice(0, VISIBLE_REQUEST_COUNT)
  const hasMore = requests.length > VISIBLE_REQUEST_COUNT

  return (
    <div className='bg-white border border-[#bfbfbf] rounded-[8px] overflow-hidden flex flex-col gap-[10px] p-4'>
      <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4'>
        <div className='flex flex-col gap-[11px] leading-6 sm:w-[302px] shrink-0'>
          <p className='!font-semibold !font-weight:600 text-[18px] text-black leading-6'>
            {event.bookingName}
          </p>
          <span className='text-[16px] text-[#8c8c8c] font-normal leading-6'>
            Booked by: {event.bookedBy}
          </span>
          <span className='text-[16px] text-[#8c8c8c] font-normal leading-6'>
            Date: {event.date}
          </span>
        </div>

        <div className='flex flex-col gap-[10px] items-start flex-1'>
          {event.invoiceAmount && (
            <div className='flex gap-[10px] items-center text-[16px] text-black font-normal leading-6'>
              <span>Invoice amount:</span>
              <span>{event.invoiceAmount}</span>
            </div>
          )}
          <div className='flex flex-col gap-[11px]'>
            {event.location && (
              <div className='flex gap-[11px] items-center'>
                <LocationOutlinedIcon
                  width={16}
                  height={16}
                  className='w-4 h-4 shrink-0 color-[#8c8c8c]'
                />
                <span className='text-[16px] text-[#8c8c8c] font-normal leading-6'>
                  {event.location}
                </span>
              </div>
            )}
            {event.capacity && (
              <div className='flex gap-[11px] items-center'>
                <PersonOutlineIcon
                  width={16}
                  height={16}
                  className='w-4 h-4 shrink-0 color-[#8c8c8c]'
                />
                <span className='text-[16px] text-[#8c8c8c] font-normal leading-6'>
                  {event.capacity}
                </span>
              </div>
            )}
          </div>
        </div>

        {isSelected ? (
          <button
            type='button'
            onClick={onSelect}
            className={`border border-[#72c040] rounded-[4px] px-4 py-2 font-[Montserrat]
              font-semibold text-base leading-6 text-[#595959] cursor-pointer
              flex items-center gap-2 shrink-0 transition-all self-start`}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#72c040' }} />
            <span>Selected</span>
          </button>
        ) : (
          <button
            type='button'
            onClick={onSelect}
            className={`border border-black rounded-[4px] px-4 py-2 font-[Montserrat]
              font-semibold text-base leading-6 text-[#595959] cursor-pointer
              shrink-0 w-[128px] text-center transition-all hover:border-[#595959] self-start`}>
            Select
          </button>
        )}
      </div>

      <div className='border-t border-[#bfbfbf] flex flex-col gap-[10px] pt-2'>
        <div className='flex gap-[11px] items-center'>
          <ArrowRightAltIcon sx={{ fontSize: 16, color: '#af231c' }} />
          <span className='font-semibold font-weight:700 text-[16px] text-black leading-6'>
            {event.bookedVenue
              ? 'Venue booked:'
              : "In progress: We're working on sourcing your venues right now"}
          </span>
        </div>
        {event.bookedVenue && (
          <div className='flex items-center gap-2 border border-[#D9D9D9] rounded-[8px] p-2'>
            <div
              className='w-[66px] h-[60px] rounded-[8px] shrink-0 bg-[#D9D9D9] bg-cover bg-center'
              style={
                event.bookedVenue.imageUrl
                  ? { backgroundImage: `url("${event.bookedVenue.imageUrl}")` }
                  : undefined
              }
            />
            <div className='flex-1 min-w-0 px-2'>
              <p className='font-semibold text-base text-black truncate'>
                {event.bookedVenue.spaceName}
              </p>
              <div className='flex items-center gap-1'>
                <LocationOutlinedIcon
                  width={16}
                  height={16}
                  className='w-4 h-4 shrink-0 color-[#8c8c8c]'
                />
                <span className='text-base text-[#595959] truncate'>
                  {event.bookedVenue.location ?? event.bookedVenue.venueName}
                </span>
              </div>
            </div>
          </div>
        )}

        {!!requests.length && (
          <div className='flex flex-col gap-2'>
            <span className='font-normal text-[16px] text-black leading-6'>You requested:</span>
            <div className='flex flex-col gap-2'>
              {visibleRequests.map((r, i) => (
                <div
                  key={i}
                  className='flex items-center gap-2 border border-[#D9D9D9] rounded-[8px] p-2'>
                  <div
                    className='w-[66px] h-[60px] rounded-[8px] shrink-0 bg-[#D9D9D9] bg-cover bg-center'
                    style={r.imageUrl ? { backgroundImage: `url("${r.imageUrl}")` } : undefined}
                  />
                  <div className='flex-1 min-w-0 px-2'>
                    <p className='font-semibold text-base text-black truncate'>{r.spaceName}</p>
                    <div className='flex items-center gap-1'>
                      <LocationOutlinedIcon
                        width={16}
                        height={16}
                        className='w-4 h-4 shrink-0 color-[#8c8c8c]'
                      />
                      <span className='text-base text-[#595959] truncate'>
                        {r.location ?? r.venueName}
                      </span>
                    </div>
                  </div>
                  <div className='flex items-center gap-1 bg-[#EBEBEB] rounded-[8px] px-4 py-2 shrink-0'>
                    {r.declinedReason ? (
                      <>
                        <CloseIcon sx={{ fontSize: 12 }} />
                        <span className='text-xs whitespace-nowrap'>
                          Declined – {DECLINED_REASON_LABELS[r.declinedReason] ?? 'Not available'}
                        </span>
                      </>
                    ) : (
                      <>
                        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                        <span className='text-xs whitespace-nowrap'>
                          Our team is reviewing this request
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {hasMore && !showAll && (
              <button
                type='button'
                onClick={() => setShowAll(true)}
                className={`text-base text-[#1270B8] font-semibold text-left
                  cursor-pointer bg-transparent border-none p-0`}>
                + Show {requests.length - VISIBLE_REQUEST_COUNT} more requested venues
              </button>
            )}
            {showAll && hasMore && (
              <button
                type='button'
                onClick={() => setShowAll(false)}
                className={`text-base text-[#1270B8] font-semibold text-left
                  cursor-pointer bg-transparent border-none p-0`}>
                Show less
              </button>
            )}
          </div>
        )}

        {event.supplierName && (
          <div className='flex gap-[11px] items-center'>
            <div className='w-4 shrink-0' />
            <span className='font-normal text-[16px] text-black leading-6'>Supplier booked:</span>
            <span className='font-normal text-[16px] text-[#141414] leading-6'>
              {event.supplierName}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default PreviousEventCard
